import { z } from 'zod';

/**
 * A moment of a calendar event: an instant with offset (`2026-11-05T08:10:00-03:00`), a floating
 * wall clock (`2026-11-05T14:00`, no zone) or a whole day (`2026-11-05`).
 */
export const CalendarTimeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('instant'), at: z.string() }),
  z.object({ kind: z.literal('floating'), local: z.string() }),
  z.object({ kind: z.literal('date'), date: z.string() }),
]);
export type CalendarTime = z.infer<typeof CalendarTimeSchema>;

/**
 * One reservation of the traveler's calendar (the ticket-issuance e-mail's schema.org markup and
 * `.ics`). voucher-events builds this language-neutral photo; the conversation localizes it into
 * a `CalendarEntry`.
 */
export const TripCalendarEventSchema = z.object({
  /** `${voucherId}`, or `${voucherId}:${flightNumber}:${YYYY-MM-DD}` for one flight of a leg. */
  reservationId: z.string().min(1),
  /** Bumped on every change so calendars replace the earlier copy. */
  sequence: z.number().int().min(0),
  status: z.enum(['confirmed', 'cancelled']),
  start: CalendarTimeSchema,
  end: CalendarTimeSchema,
  /** Airline locator (flight) or supplier confirmation (hotel/car). */
  code: z.string().nullable(),
  detail: z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('flight'),
      airlineCode: z.string().nullable(),
      airlineName: z.string().nullable(),
      flightNumber: z.string(),
      from: z.string(),
      to: z.string(),
      seat: z.string().nullable(),
    }),
    z.object({
      kind: z.literal('hotel'),
      name: z.string().nullable(),
      address: z.string().nullable(),
      city: z.string().nullable(),
      checkInHour: z.string().nullable(),
      checkOutHour: z.string().nullable(),
    }),
    z.object({
      kind: z.literal('car'),
      company: z.string().nullable(),
      model: z.string().nullable(),
      pickup: z.string().nullable(),
      dropoff: z.string().nullable(),
    }),
  ]),
});
export type TripCalendarEvent = z.infer<typeof TripCalendarEventSchema>;

/** What the conversation hands the e-mail: the event plus its localized text. PII. */
export const CalendarEntrySchema = TripCalendarEventSchema.extend({
  title: z.string().min(1),
  description: z.string(),
  location: z.string().nullable(),
  url: z.string().nullable(),
  /** The traveler the reservation is under. */
  underName: z.string().nullable(),
});
export type CalendarEntry = z.infer<typeof CalendarEntrySchema>;

const CalendarEntriesSchema = z.array(CalendarEntrySchema);

/** Entries from a connector var (JSON); `[]` when absent, invalid or empty. Never throws. */
export function parseCalendarEntries(json: string | undefined): CalendarEntry[] {
  if (!json) return [];
  try {
    const parsed = CalendarEntriesSchema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}
